import { jwtDecode } from 'jwt-decode';
import { Routes } from '@/routes';
import { UserHttp } from '@/services';
import { useLocalStorage } from '@/hooks';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { logout } from './utils';
import profilePhoto from '@/assets/images/user.png';

export interface UserContextType {
  error: string;
  isAuth: boolean;
  loading: boolean;
  token: string;
  refreshToken: string;
  profile: UserProfileEntity;
  roles: SamadRole[];
  profileImage: string;
  refresh: () => void;
  login: (payload: UserHttp.LoginPayload) => Promise<any>;
  loginCas: (access: string, refresh?: string) => void;
  logout: () => void;
}

export const UserContext = createContext<UserContextType>({
  loading: false,
  error: null,
  isAuth: false,
  refresh: null,
  login: null,
  loginCas: null,
  token: null,
  refreshToken: null,
  logout: null,
  roles: [],
  profile: {} as any,
  profileImage: profilePhoto
});

const isOnLoginOrRestPasswordPage = () => window.location.pathname.includes(Routes.Login) || window.location.pathname.includes(Routes.ResetPassword);

export const UserContextProvider = ({ children }) => {

  const [token, setToken] = useLocalStorage('TOKEN', null);
  const [refreshToken, setRefreshToken] = useLocalStorage('REFRESH_TOKEN', null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuth, setIsAuth] = useState(false);
  const [profile, setProfile] = useState<UserProfileEntity>({} as any);
  const [profileImage] = useState<any>(profilePhoto);

  const fetchUser = useCallback(() => {
    if (token == null) {
      setLoading(false);
      setIsAuth(false);
      if (!isOnLoginOrRestPasswordPage()) {
        if (window.location.pathname != '/') {
          window.location.href = `${Routes.Login}?next=${window.location.pathname}`;
          return;
        }
        window.location.href = Routes.Login;
      }
      return;
    }
    setLoading(true);
    UserHttp.getUserProfile()
      .then(res => {
        setProfile(res?.data?.payload);
        setIsAuth(true);
      })
      .catch((err) => {
        console.error(err);
        setError(err);
        setIsAuth(false);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(fetchUser, [fetchUser]);

  const login = useCallback(async ({ username, password, captchaText, nonce, validation }: UserHttp.LoginPayload) => {
    const response = await UserHttp.authorize({ username, password, captchaText, nonce, validation });
    setToken(response.data.access_token);
    setRefreshToken(response.data.refresh_token);
    // fetchUser();
  }, [fetchUser]);

  const loginCas = useCallback((accessToken: string, refreshToken?: string) => {
    setToken(accessToken);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
  }, [fetchUser]);

  const roles: SamadRole[] = useMemo(() => {
    try {
      const decoded = jwtDecode(token) as any;
      return decoded?.authorities ?? [];
    }
    catch (e) {
      return [];
    }
  }, [token]);

  return (
    <UserContext.Provider value={{
      login,
      loginCas,
      isAuth,
      error,
      roles,
      token,
      refreshToken,
      profile,
      profileImage,
      refresh: fetchUser,
      loading,
      logout: () => logout(profile?.username)
    }}>
      {children}
    </UserContext.Provider>
  );
};
